/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import buildCompanyEnvironmentRows from '@/redux/selectors/pieveSelectors/lib/buildCompanyEnvironmentRows.js';
import getThreadsById from '@/lib/threads/getThreadsById.js';
import getCompanyScopedArray from '@/redux/selectors/lib/getCompanyScopedArray.js';
import buildThreadContentItems from '@/lib/threads/buildThreadContentItems.js';
import getCanonicalThreads from '@/redux/selectors/lib/getCanonicalThreads.js';
import getThreadContentByThreadId from '@/redux/selectors/lib/getThreadContentByThreadId.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@/redux/selectors/pieveSelectors/constants.js';
import { activeCompanyId as selectedActiveCompanyId } from '@/redux/selectors/userSelectors.js';

export const pieveState = (state) => state?.pieve;
export const sidebar = (state) => state?.pieve?.sidebar;
export const hasCanonicalThreadsArray = (state) => Array.isArray(state?.pieve?.threads);
export const storedThreadMetaById = (state) => state?.pieve?.threadMetaById || EMPTY_OBJECT;

export const threads = createSelector([pieveState, selectedActiveCompanyId], (value, activeCompanyId) => {
  return getCompanyScopedArray({
    items: getCanonicalThreads(value),
    ownerCompanyId: value?.threadsScopeCompanyId,
    activeCompanyId
  });
});

export const threadMetaById = createSelector([threads, storedThreadMetaById, hasCanonicalThreadsArray], (threadItemsValue, storedThreadMetaByIdValue, currentHasCanonicalThreadsArray) => {
  const mappedThreadsById = getThreadsById(threadItemsValue);

  if (mappedThreadsById !== EMPTY_OBJECT && Object.keys(mappedThreadsById).length > 0) {
    return mappedThreadsById;
  }

  if (currentHasCanonicalThreadsArray) {
    return EMPTY_OBJECT;
  }

  return storedThreadMetaByIdValue;
});

const rawThreadContent = (state) => state?.pieve?.threadContent;
export const threadContentItems = createSelector([rawThreadContent], (threadContentValue) => {
  return buildThreadContentItems(threadContentValue);
});

export const threadContentMap = createSelector([threadContentItems], (threadContentItemsValue) => {
  return getThreadContentByThreadId(threadContentItemsValue);
});

export const schedules = (state) => state?.pieve?.scheduleItems || EMPTY_ARRAY;
export const triggers = (state) => state?.pieve?.triggerItems || EMPTY_ARRAY;
export const workflows = createSelector([(state) => state?.pieve?.workflows || EMPTY_ARRAY, (state) => state?.pieve?.workflowsScopeCompanyId || null, selectedActiveCompanyId], (items, ownerCompanyId, activeCompanyId) => {
  return getCompanyScopedArray({ items, ownerCompanyId, activeCompanyId });
});
/* True once GET_WORKFLOWS has landed for the active company. The workflow catalog and the thread
   list arrive on separate requests, so without this a real workflow whose thread loaded first would
   flash as a planning build for one render. */
export const workflowsLoadedForActiveCompany = createSelector([(state) => state?.pieve?.workflowsScopeCompanyId || null, selectedActiveCompanyId], (ownerCompanyId, activeCompanyId) => {
  return Boolean(ownerCompanyId) && ownerCompanyId === activeCompanyId;
});
export const companyThreads = createSelector([(state) => state?.pieve?.companyThreads || EMPTY_ARRAY, (state) => state?.pieve?.companyThreadsScopeCompanyId || null, selectedActiveCompanyId], (items, ownerCompanyId, activeCompanyId) => {
  return getCompanyScopedArray({ items, ownerCompanyId, activeCompanyId });
});
export const workflowConnections = (state) => state?.pieve?.workflowConnections || EMPTY_ARRAY;
export const companyEnvironments = (state) => state?.pieve?.companyEnvironments || EMPTY_ARRAY;
// The name of the environment a credential is tagged with (`meta.environmentId`); '' when untagged or unknown.
export const companyEnvironmentName = (state, environmentId) => {
  const id = String(environmentId || '').trim();

  return id ? companyEnvironments(state).find((environment) => environment?.id === id)?.name || '' : '';
};
// The org-settings view of that list: predefined derived from the name, Gravity and Pieve usage summed.
export const companyEnvironmentRows = createSelector([companyEnvironments], (environments) => {
  return buildCompanyEnvironmentRows({ environments });
});
export const workflowActions = (state) => state?.pieve?.workflowActions || EMPTY_ARRAY;
export const workflowActionsThreadId = (state) => state?.pieve?.workflowActionsThreadId || null;
export const workflowActionsLatestRunStartedOn = (state) => state?.pieve?.workflowActionsLatestRunStartedOn || null;
export const threadAppConnectionStatus = (state) => state?.pieve?.threadAppConnectionStatus || EMPTY_ARRAY;
export const threadAppConnectionStatusThreadId = (state) => state?.pieve?.threadAppConnectionStatusThreadId || null;
export const workflowConnectionsWorkflowId = (state) => state?.pieve?.workflowConnectionsWorkflowId || null;
export const workflowWebhookSamples = (state) => state?.pieve?.workflowWebhookSamples || EMPTY_ARRAY;
export const workflowWebhookSamplesWorkflowId = (state) => state?.pieve?.workflowWebhookSamplesWorkflowId || null;
// Every stored workflow row, unscoped; `workflows` below is the company-scoped list.
export const rawWorkflowRows = (state) => state?.pieve?.workflows || EMPTY_ARRAY;
export const workflowStepPendingEdits = (state) => state?.pieve?.workflowStepPendingEdits || EMPTY_ARRAY;
export const workflowRunList = (state) => state?.pieve?.workflowRunList || EMPTY_ARRAY;
export const workflowRunListPaging = (state, workflowId) => state.pieve.workflowRunListPaging[workflowId] || null;
export const workflowRunDailyCountsList = (state) => state?.pieve?.workflowRunDailyCountsList || EMPTY_ARRAY;
export const workflowRunOperationsList = (state) => state?.pieve?.workflowRunOperationsList || EMPTY_ARRAY;
export const workflowOperationsList = (state) => state?.pieve?.workflowOperationsList || EMPTY_ARRAY;
export const workflowRecentActivityChartDailyCounts = (state) => state?.pieve?.workflowRecentActivityChartDailyCounts || EMPTY_ARRAY;
export const workflowRunTimeMachineList = (state) => state?.pieve?.workflowRunTimeMachineList || EMPTY_ARRAY;
export const appsMetadataItems = (state) => state?.pieve?.appsMetadataItems || EMPTY_ARRAY;
export const appVersions = (state) => state.pieve.appVersions;
export const appsMetadataEtag = (state) => String(state?.pieve?.appsMetadataEtag || '').trim() || null;

export const appsMetadataIncludesHidden = (state) => state?.pieve?.appsMetadataIncludesHidden === true;
export const workflowVersionsList = (state) => state?.pieve?.workflowVersionsList || EMPTY_ARRAY;
export const publicThread = (state) => state?.pieve?.publicThread || null;
export const isPublicThread = (state) => publicThread(state) !== null;
